// ./src/components/features/CreateEventWizard/steps/GiftsStep.tsx
//
// "Gifts" — for the types where guests usually bring one (ROADMAP A2h #140, owner 2026-10-09: a baby shower, a wedding, a
// birthday, especially a child's). Which types those are is the catalog's answer (`EventSetupType.gifts`); the shell
// shows this step only then (`giftsNeedStep`).
//
// Two answers, either, both or neither (`giftRegistry.ts`):
//   - REGISTRY LINKS: a name and a web address per registry — saved on Next as ONE Link Buttons block on the home page;
//   - A GIFT LIST: the Sign-up list feature, switched on here (its step follows), so guests pick a gift and nothing is
//     bought twice.
// ⚠ Nothing is written while the host types: the shell saves the step on Next / Finish (`saveGifts`). A half-filled row
//   holds Next, and is marked at its own inputs.
// ⚠ Switching the gift list OFF gives back only what this step changed and nothing used yet — while the feature is still
//   unsaved (`giftFeatureEdited`). Once saved, the Sign-up list step may hold items: the feature stays, and switching it
//   off is the Features step's business.
// ⚠ On a revisit the rows are READ BACK from the block the step made, so what the page holds (a label changed in the page
//   editor) is what the host edits — and a rewrite keeps the rest of the block (`mergeGiftButtons`).

import React, { useCallback, useEffect, useRef } from 'react';
import { ActionIcon, Box, Button, Group, Paper, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { OpResult } from '@sdflc/api-helpers';
import { useTranslation } from 'react-i18next';

import { useOptionalEventSetupCatalogContext } from '@/contexts';
import { EVENT_FEATURES } from '@/config/eventSetup.generated';
import { useFeatureGate } from '@/hooks';
import { featuresAfterRules } from '@/utils/eventFeatureRules';
import { AppIcon, TextField } from '@/components/generic';

import { useEventWizard } from '../WizardContext';
import { WizardSelectCard } from '../components/WizardSelectCard';
import {
  GiftRegistryLink,
  SIGNUP_LIST_FEATURE as SIGNUP_LIST,
  emptyGiftLink,
  giftLinkProblems,
  giftLinkRows,
  giftLinkStarted,
  giftLinksOfBlock,
  isGiftList,
} from '../giftRegistry';
import { readWebsiteState } from '../websiteBlocksApply';

/** The plan feature the Sign-up list needs (the generated catalog — never restated here). */
const SIGNUP_PLAN_FEATURE = EVENT_FEATURES.find((f) => f.code === SIGNUP_LIST)?.planFeatureCode ?? null;

/** How many of the type's gift ideas the gift-list card names. */
const IDEAS_SHOWN = 3;

export const GiftsStep: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { draft, patchDraft, saving, createdEventId } = useEventWizard();
  const catalog = useOptionalEventSetupCatalogContext();
  const { hasFeature } = useFeatureGate();

  const type = catalog?.findType(draft.eventCategoryId) ?? null;
  const parent = catalog?.parentOf(type) ?? null;
  const ideas = (type?.gifts?.ideas ?? []).slice(0, IDEAS_SHOWN);

  const rows = giftLinkRows(draft.giftLinks);
  // ⓘ Marked only after a refused Next — not while the host is still typing the second half of a row.
  const problems = draft.giftLinksTried ? giftLinkProblems(rows) : {};

  const featuresOn = draft.featuresOn ?? [];
  const signupOn = featuresOn.includes(SIGNUP_LIST);
  // ⚠ Whether this event CAN have a sign-up list is the rules' answer (a save-the-date cannot), never decided here.
  const signupAllowed = signupOn || featuresAfterRules(type, parent, catalog?.rules ?? [], draft.eventMode, [...featuresOn, SIGNUP_LIST]).includes(SIGNUP_LIST);
  const giftList = isGiftList(draft);
  // ⓘ Premium is said, not enforced here: the list may be chosen now (as on the Features step) — adding gifts to it is
  //   what the plan gates, on the next step.
  const premium = !!SIGNUP_PLAN_FEATURE && !hasFeature(SIGNUP_PLAN_FEATURE, { eventId: createdEventId });

  // A revisit READS BACK the block this step made — unless the host has already started editing the rows.
  const editedRef = useRef(!!draft.giftLinksEdited);

  editedRef.current = !!draft.giftLinksEdited;

  const widgetId = draft.giftLinksWidgetId ?? null;

  useEffect(() => {
    if (!createdEventId || !widgetId || editedRef.current) {
      return undefined;
    }

    let current = true;

    void readWebsiteState(createdEventId).then((state) => {
      // ⓘ A failed read keeps the rows as the step last saved them — nothing is written unless the host edits.
      if (!current || editedRef.current || state instanceof OpResult) {
        return;
      }

      const block = state.widgets.find((w) => w.id === widgetId);

      // A block removed in the page editor since: the step starts empty, and makes a new one only if the host adds links.
      patchDraft(block ? { giftLinks: giftLinksOfBlock(block.config?.links) } : { giftLinks: [], giftLinksWidgetId: null });
    });

    return () => {
      current = false;
    };
  }, [createdEventId, widgetId, patchDraft]);

  const setRows = useCallback(
    (next: GiftRegistryLink[]) => {
      patchDraft({ giftLinks: next, giftLinksEdited: true });
    },
    [patchDraft],
  );

  // ONE handler for every row's two inputs — the row and the half are in the field's name (`label:2`, `url:0`).
  const handleChange = useCallback(
    (name: string | undefined, value: string) => {
      const [part, at] = String(name ?? '').split(':');
      const index = Number(at);

      if ((part !== 'label' && part !== 'url') || !Number.isInteger(index)) {
        return;
      }

      setRows(rows.map((row, i) => (i === index ? { ...row, [part]: value } : row)));
    },
    [rows, setRows],
  );

  const addRow = () => {
    setRows([...rows, emptyGiftLink()]);
  };

  // The last row is emptied rather than removed — the section always has a row to type into.
  const removeRow = (index: number) => {
    setRows(rows.length > 1 ? rows.filter((_, i) => i !== index) : [emptyGiftLink()]);
  };

  const toggleGiftList = () => {
    if (giftList) {
      // ⚠ Undone only while nothing could have used it: the feature this step switched on and has not saved yet.
      if (draft.giftSignup === 'added' && draft.giftFeatureEdited) {
        patchDraft({ giftSignup: null, featuresOn: featuresOn.filter((code) => code !== SIGNUP_LIST), giftFeatureEdited: false });

        return;
      }

      patchDraft({ giftSignup: null });

      return;
    }

    if (signupOn) {
      patchDraft({ giftSignup: 'existing' });

      return;
    }

    patchDraft({ giftSignup: 'added', featuresOn: [...featuresOn, SIGNUP_LIST], giftFeatureEdited: true });
  };

  return (
    <Stack gap="lg">
      <Text size="md" c="dimmed">
        {type?.gifts
          ? t('events:wizard.gifts.intro', {
              defaultValue:
                'Guests usually bring a gift to an event like this. Show them where you are registered, let them pick a gift from a list so nothing is bought twice — or both. Leave this step empty to say nothing about gifts.',
            })
          : // ⓘ Shown for a type that does not usually take gifts only because this step already put something on the event.
            t('events:wizard.gifts.introKept', {
              defaultValue:
                'You added gifts to this event earlier. Change your registries or the gift list here — or empty this step to say nothing about gifts.',
            })}
      </Text>

      <Stack gap="sm">
        <Title order={4}>{t('events:wizard.gifts.registryTitle', { defaultValue: 'Gift registries' })}</Title>
        <Text size="md" c="dimmed">
          {t('events:wizard.gifts.registryHint', {
            defaultValue: 'Each registry becomes a button on your event page, under “Gift registry”.',
          })}
        </Text>

        {rows.map((row, index) => (
          <Paper key={index} withBorder radius="md" p="md">
            <Stack gap="sm">
              <Group justify="space-between" wrap="nowrap">
                <Text size="md" fw={600}>
                  {t('events:wizard.gifts.registryN', { defaultValue: 'Registry {{n}}', n: index + 1 })}
                </Text>
                {/* ⓘ Not on the one empty row — there is nothing to remove. */}
                {rows.length > 1 || giftLinkStarted(row) ? (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="lg"
                    onClick={() => removeRow(index)}
                    disabled={saving}
                    aria-label={t('events:wizard.gifts.removeRegistryN', { defaultValue: 'Remove registry {{n}}', n: index + 1 })}
                  >
                    <AppIcon iconName="Trash" size={18} />
                  </ActionIcon>
                ) : null}
              </Group>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
                <TextField
                  name={`label:${index}`}
                  label={t('events:wizard.gifts.label', { defaultValue: 'Name on the button' })}
                  placeholder={t('events:wizard.gifts.labelPlaceholder', { defaultValue: 'e.g. Our baby registry' })}
                  value={row.label}
                  onChange={handleChange}
                  disabled={saving}
                  error={problems[index]?.label ? t('events:wizard.gifts.labelMissing', { defaultValue: 'Enter the name on the button' }) : undefined}
                />
                <TextField
                  name={`url:${index}`}
                  label={t('events:wizard.gifts.url', { defaultValue: 'Web address' })}
                  placeholder="https://"
                  value={row.url}
                  onChange={handleChange}
                  disabled={saving}
                  inputMode="url"
                  error={problems[index]?.url ? t('events:wizard.gifts.urlMissing', { defaultValue: 'Enter the web address' }) : undefined}
                />
              </SimpleGrid>
            </Stack>
          </Paper>
        ))}

        <Group>
          <Button variant="light" size="md" leftSection={<AppIcon iconName="Plus" size={18} />} onClick={addRow} disabled={saving}>
            {t('events:wizard.gifts.addRegistry', { defaultValue: 'Add another registry' })}
          </Button>
        </Group>
      </Stack>

      <Stack gap="sm">
        <Title order={4}>{t('events:wizard.gifts.listTitle', { defaultValue: 'A gift list' })}</Title>
        {signupAllowed ? (
          <WizardSelectCard
            icon="ListCheck"
            color="grape"
            title={t('events:wizard.gifts.listCard', { defaultValue: 'Let guests choose a gift from a list' })}
            description={
              signupOn && draft.giftSignup !== 'added'
                ? t('events:wizard.gifts.listCardExisting', {
                    defaultValue: 'Your sign-up list becomes the gift list. Add the gifts on the next step; each guest picks one, so nothing is bought twice.',
                  })
                : t('events:wizard.gifts.listCardAdd', {
                    defaultValue: 'Uses the Sign-up list. Add the gifts on the next step; each guest picks one, so nothing is bought twice.',
                  })
            }
            selected={giftList}
            disabled={saving}
            onClick={toggleGiftList}
            extra={
              ideas.length > 0 || premium ? (
                <Stack gap={4}>
                  {ideas.length > 0 ? (
                    <Text size="md" c="dimmed">
                      {t('events:wizard.gifts.ideas', { defaultValue: 'Ideas: {{ideas}}', ideas: ideas.join(', ') })}
                    </Text>
                  ) : null}
                  {premium ? (
                    <Group gap={6} align="flex-start" wrap="nowrap">
                      <Box pt={3}>
                        <AppIcon iconName="Crown" size={16} color="var(--mantine-color-yellow-6)" />
                      </Box>
                      <Text size="md" c="dimmed">
                        {t('events:wizard.gifts.listPremium', {
                          defaultValue: 'Premium — adding gifts to the list needs a plan upgrade. You can choose it now and upgrade when you are ready.',
                        })}
                      </Text>
                    </Group>
                  ) : null}
                </Stack>
              ) : null
            }
          />
        ) : (
          <Text size="md" c="dimmed">
            {t('events:wizard.gifts.listUnavailable', {
              defaultValue: 'A gift list uses the Sign-up list, which an event that guests join this way cannot have.',
            })}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
